'use client';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { deprecatedProp } from '../core/deprecated-prop.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { RenderComp } from '../core/render-comp.component.js';

const propTypes = {
    /**
   * Visual type of banner
   * Available options: "card", "full-width-image"
   */ variant: PropTypes.oneOf([
        'card',
        'full-width-image'
    ]),
    /**
   * Whether the Banner has two images
   */ twoImages: PropTypes.bool,
    /**
   * Whether the Banner has multiple discounts
   */ multiColumnOffer: PropTypes.bool,
    /**
   * Height of the Banner
   * Available options: "standard", "short"
   */ height: PropTypes.oneOf([
        'standard',
        'short'
    ]),
    /**
   * @deprecated
   * The text will be by default center aligned on extra small screen size devices and to maintain the consistent user experience, we are deprecating this prop.
   *
   * How the text itself should be aligned horizontally within the Banner, when on mobile.
   * Available options: "left", "center"
   */ textHorizontalAlignOnExtraSmall: deprecatedProp(PropTypes.oneOf([
        'left',
        'center'
    ]), 'The text will be by default center aligned on extra small screen size devices and to maintain the consistent user experience, we are deprecating this prop.'),
    /**
   * How the text box should be aligned horizontally within the Banner.
   * Available options: "left", "center", "right"
   */ textBoxHorizontalAlign: PropTypes.oneOf([
        'left',
        'center',
        'right'
    ]),
    /**
   * Specifies if the Banner's layout should adjust to fit a left hand navigation menu
   */ withLeftHandNavigation: PropTypes.bool,
    /**
   * Specifies if the Banner's secondary image should be shown if we are only showing one image
   */ preferSecondaryImage: PropTypes.bool,
    /**
   * Hides all image(s) on Extra-Small screens
   */ hideImageOnExtraSmall: PropTypes.bool,
    /**
   * @deprecated
   * Set `imageFocalPoint` on `BannerImage` or `BannerSecondaryImage` instead.
   *
   * Sets the focal point if the image gets clipped. (X-axis first, then Y-axis)
   */ imageFocalPoint: deprecatedProp(PropTypes.oneOf([
        'left top',
        'left center',
        'left bottom',
        'center top',
        'center center',
        'center bottom',
        'right top',
        'right center',
        'right bottom'
    ]), "This prop is deprecated and will be removed in a future release. Please set 'imageFocalPoint' on 'BannerImage' or 'BannerSecondaryImage' instead.")
};
const propKeysToRemove = Object.keys(propTypes);
const Banner = renderWithRef('Banner', propTypes, (props, ref)=>{
    useComponentStylesLoaded('Banner', SWAN_STYLE_KEY_MAP.banner);
    const { children, height, textHorizontalAlignOnExtraSmall, textBoxHorizontalAlign = 'left', variant = 'card', twoImages, multiColumnOffer, withLeftHandNavigation, preferSecondaryImage, hideImageOnExtraSmall, imageFocalPoint } = props;
    const classNameSet = new Set([
        'swan-banner'
    ]);
    classNameSet.add(`swan-banner-${variant}`);
    if (textHorizontalAlignOnExtraSmall) classNameSet.add(`swan-banner-text-horizontal-align-on-extra-small-${textHorizontalAlignOnExtraSmall}`);
    if (textBoxHorizontalAlign) classNameSet.add(`swan-banner-text-box-horizontal-align-${textBoxHorizontalAlign}`);
    if (height === 'short') classNameSet.add('swan-banner-short');
    if (twoImages && variant === 'card') classNameSet.add('swan-banner-two-images');
    if (multiColumnOffer) classNameSet.add('swan-banner-multi-column-offer');
    if (withLeftHandNavigation && variant === 'card' && textBoxHorizontalAlign === 'left') classNameSet.add('swan-banner-with-left-hand-navigation');
    if (preferSecondaryImage) classNameSet.add('swan-banner-prefer-secondary-image');
    if (hideImageOnExtraSmall) classNameSet.add('swan-banner-hide-image-on-extra-small');
    if (imageFocalPoint) classNameSet.add(`swan-banner-image-focal-point-${imageFocalPoint.replace(/ +/g, '-')}`);
    return /*#__PURE__*/ React.createElement(RenderComp, {
        root: "div",
        forwardedRef: ref,
        propKeysToRemove: propKeysToRemove,
        classNames: classNameSet,
        props: props
    }, children);
});

export { Banner };
